/**
 * Race overlays: waiting room (host controls), 3-2-1-GO countdown, results.
 * All French text lives here, none in the game logic.
 */

import type { PlayerPublicInfo, RaceResultRow, RaceStatus } from '@microligue/shared';
import { RACE_LAPS_BOUNDS, SURFACE_IDS, SURFACE_LABELS, TICK_RATE } from '@microligue/shared';
import { carSwatch } from '../car/svg';
import { formatLapTime } from './hud';
import { SURFACE_COLORS } from './surfaces';

export interface RaceUiCallbacks {
  onSetLaps: (laps: number) => void;
  onStart: () => void;
  onBackToLobby: () => void;
  onLeaveRoom: () => void;
}

export class RaceUi {
  private readonly root: HTMLElement;
  private mode: 'hidden' | 'waiting' | 'countdown' | 'results' = 'hidden';
  private lastCountdownLabel = '';
  /**
   * Waiting card reduced to its title bar. Persists across the frequent
   * re-renders (roster changes) so it doesn't spring back open, and lets the
   * driver clear the card off the road while testing without leaving the room.
   */
  private waitingCollapsed = false;

  constructor(
    doc: Document,
    private readonly callbacks: RaceUiCallbacks,
  ) {
    const el = doc.getElementById('race-overlay');
    if (!el) throw new Error('Élément introuvable : #race-overlay');
    this.root = el;
  }

  hide(): void {
    if (this.mode === 'hidden') return;
    this.mode = 'hidden';
    this.root.hidden = true;
    this.root.replaceChildren();
  }

  /** Waiting room: free driving, host lines everyone up when ready. */
  showWaiting(
    race: RaceStatus,
    players: PlayerPublicInfo[],
    selfId: number,
    circuitName: string,
  ): void {
    this.mode = 'waiting';
    this.root.hidden = false;
    this.root.className = 'race-overlay-corner';

    const card = document.createElement('div');
    card.className = 'race-card';

    // Title bar, always visible — carries the collapse toggle so the card can be
    // folded down to just this line (frees the road) and unfolded with one click.
    const header = document.createElement('div');
    header.className = 'race-card-header';
    const title = document.createElement('h3');
    title.textContent = 'Salon d’attente';
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'race-card-toggle';
    toggle.addEventListener('click', () => {
      this.waitingCollapsed = !this.waitingCollapsed;
      this.applyWaitingCollapsed(card, toggle);
    });
    header.append(title, toggle);

    // Everything below the title bar lives in the body, hidden when collapsed.
    const body = document.createElement('div');
    body.className = 'race-card-body';
    card.append(header, body);

    const sub = document.createElement('div');
    sub.className = 'race-sub';
    sub.textContent = `${circuitName} · échauffement libre`;
    body.append(sub);

    const ghostHint = document.createElement('div');
    ghostHint.className = 'race-sub';
    ghostHint.textContent = '👻 G : fantôme du record (circuit / perso)';
    body.append(ghostHint);

    const list = document.createElement('div');
    list.className = 'race-players';
    for (const p of players) {
      const row = document.createElement('span');
      row.className = 'race-player' + (p.connected ? '' : ' race-player-off');
      const name = document.createElement('span');
      name.textContent =
        p.pseudo + (p.id === race.hostId ? ' 👑' : '') + (p.id === selfId ? ' (vous)' : '');
      row.append(carSwatch(p.design, 30), name);
      list.appendChild(row);
    }
    body.appendChild(list);

    const isHost = selfId === race.hostId;
    if (isHost) {
      const lapsRow = document.createElement('label');
      lapsRow.className = 'race-laps-row';
      lapsRow.textContent = 'Tours : ';
      const laps = document.createElement('input');
      laps.type = 'number';
      laps.min = String(RACE_LAPS_BOUNDS.min);
      laps.max = String(RACE_LAPS_BOUNDS.max);
      laps.value = String(race.laps);
      laps.addEventListener('change', () => {
        const v = Math.round(Number(laps.value));
        if (Number.isFinite(v)) this.callbacks.onSetLaps(v);
      });
      lapsRow.appendChild(laps);

      const start = document.createElement('button');
      start.type = 'button';
      start.className = 'btn-primary';
      start.textContent = '🏁 Lancer la course';
      start.addEventListener('click', () => this.callbacks.onStart());

      body.append(lapsRow, start);
    } else {
      const wait = document.createElement('div');
      wait.className = 'race-sub';
      wait.textContent = `Course en ${race.laps} tour${race.laps > 1 ? 's' : ''} — en attente de l’hôte…`;
      body.appendChild(wait);
    }

    const leave = document.createElement('button');
    leave.type = 'button';
    leave.className = 'race-leave';
    leave.textContent = '← Quitter le salon';
    leave.addEventListener('click', () => this.callbacks.onLeaveRoom());
    body.appendChild(leave);

    this.applyWaitingCollapsed(card, toggle);
    this.root.replaceChildren(card);
  }

  /** Fold/unfold the waiting card to its title bar, syncing the toggle glyph. */
  private applyWaitingCollapsed(card: HTMLElement, toggle: HTMLButtonElement): void {
    card.classList.toggle('race-card-collapsed', this.waitingCollapsed);
    toggle.textContent = this.waitingCollapsed ? '▸' : '▾';
    toggle.title = this.waitingCollapsed ? 'Afficher le salon' : 'Réduire (libère la piste)';
    toggle.setAttribute(
      'aria-label',
      this.waitingCollapsed ? 'Afficher le salon d’attente' : 'Réduire le salon d’attente',
    );
  }

  /** 3-2-1-GO. `ticksToGo` comes from the estimated server clock. */
  showCountdown(ticksToGo: number): void {
    const seconds = ticksToGo / TICK_RATE;
    const label = seconds > 2 ? '3' : seconds > 1 ? '2' : seconds > 0 ? '1' : 'GO !';
    if (this.mode !== 'countdown') {
      this.mode = 'countdown';
      this.root.hidden = false;
      this.root.className = 'race-overlay-center';
      this.root.replaceChildren();
      this.lastCountdownLabel = '';
    }
    if (label === this.lastCountdownLabel) return;
    this.lastCountdownLabel = label;

    const el = document.createElement('div');
    el.className = 'countdown' + (label === 'GO !' ? ' countdown-go' : '');
    el.textContent = label;
    this.root.replaceChildren(el);
  }

  showResults(laps: number, results: RaceResultRow[], selfId: number, isHost: boolean): void {
    this.mode = 'results';
    this.root.hidden = false;
    this.root.className = 'race-overlay-center';

    const card = document.createElement('div');
    card.className = 'results-card';

    const title = document.createElement('h2');
    title.textContent = '🏁 Résultats';
    const sub = document.createElement('div');
    sub.className = 'race-sub';
    sub.textContent = `Course en ${laps} tour${laps > 1 ? 's' : ''}`;
    card.append(title, sub);

    const table = document.createElement('table');
    table.className = 'results-table';
    const head = document.createElement('tr');
    for (const h of ['', 'Pilote', 'Temps total', 'Meilleur tour', '']) {
      const th = document.createElement('th');
      th.textContent = h;
      head.appendChild(th);
    }
    table.appendChild(head);

    for (const r of results) {
      const tr = document.createElement('tr');
      if (r.playerId === selfId) tr.className = 'results-self';

      const pos = document.createElement('td');
      pos.className = 'results-pos';
      pos.textContent = `${r.pos}`;

      const who = document.createElement('td');
      who.className = 'results-who';
      const name = document.createElement('span');
      name.textContent = r.pseudo;
      who.append(carSwatch(r.design, 34), name);

      const total = document.createElement('td');
      total.className = 'results-time';
      total.textContent = r.finished
        ? formatLapTime(r.totalTimeMs ?? 0)
        : `abandon (${r.lapsDone} tour${r.lapsDone > 1 ? 's' : ''})`;

      const best = document.createElement('td');
      best.className = 'results-time';
      best.textContent = r.bestLapMs !== null ? formatLapTime(r.bestLapMs) : '—';

      const badges = document.createElement('td');
      badges.className = 'results-badges';
      if (r.isRaceRecord) {
        const b = document.createElement('span');
        b.className = 'badge badge-record';
        b.textContent = '🏆 Record de course';
        badges.appendChild(b);
      }
      if (r.isLapRecord) {
        const b = document.createElement('span');
        b.className = 'badge badge-lap';
        b.textContent = '⚡ Record du tour';
        badges.appendChild(b);
      }

      tr.append(pos, who, total, best, badges);
      table.appendChild(tr);

      // Répartition du temps de course par surface : barre proportionnelle +
      // temps par surface (seules les surfaces réellement parcourues figurent).
      const splitTotal = SURFACE_IDS.reduce((sum, id) => sum + r.surfaceTimesMs[id], 0);
      if (splitTotal > 0) {
        const split = document.createElement('tr');
        split.className = 'results-surfaces' + (r.playerId === selfId ? ' results-self' : '');
        const cell = document.createElement('td');
        cell.colSpan = 5;
        const bar = document.createElement('div');
        bar.className = 'surface-split-bar';
        const chips = document.createElement('div');
        chips.className = 'surface-split-chips';
        for (const id of SURFACE_IDS) {
          const ms = r.surfaceTimesMs[id];
          if (ms <= 0) continue;
          const pct = Math.round((ms / splitTotal) * 100);
          const seg = document.createElement('span');
          seg.style.backgroundColor = SURFACE_COLORS[id];
          seg.style.flexGrow = String(ms);
          seg.title = `${SURFACE_LABELS[id]} : ${formatLapTime(ms)} (${pct} %)`;
          bar.appendChild(seg);
          const chip = document.createElement('span');
          chip.className = 'surface-split-chip';
          const dot = document.createElement('span');
          dot.className = 'hud-player-dot';
          dot.style.backgroundColor = SURFACE_COLORS[id];
          const txt = document.createElement('span');
          txt.textContent = `${SURFACE_LABELS[id]} ${formatLapTime(ms)}`;
          chip.append(dot, txt);
          chips.appendChild(chip);
        }
        cell.append(bar, chips);
        split.appendChild(cell);
        table.appendChild(split);
      }
    }
    card.appendChild(table);

    const actions = document.createElement('div');
    actions.className = 'results-actions';
    if (isHost) {
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'btn-primary';
      back.textContent = 'Retour au salon';
      back.addEventListener('click', () => this.callbacks.onBackToLobby());
      actions.appendChild(back);
    } else {
      const wait = document.createElement('div');
      wait.className = 'race-sub';
      wait.textContent = 'Retour au salon automatique…';
      actions.appendChild(wait);
    }
    const leave = document.createElement('button');
    leave.type = 'button';
    leave.className = 'race-leave';
    leave.textContent = '← Quitter le salon';
    leave.addEventListener('click', () => this.callbacks.onLeaveRoom());
    actions.appendChild(leave);

    card.appendChild(actions);
    this.root.replaceChildren(card);
  }
}
